import * as THREE from "three";
import { humanSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { billboardMaterial, faceCameraYaw, facingFlipSign, SPRITE_FOOT_Y } from "./WorldArt";

type Drummer = {
  mesh: THREE.Mesh;
  shadow: THREE.Mesh;
  x: number;
  z: number;
  y: number;
  facing: number;
  ready: ReturnType<typeof humanSprite>;
  hit: ReturnType<typeof humanSprite>;
  phase: number;
  period: number;
};

/**
 * Ambient drum circle: sitters in a ring on the lawn, taking turns hitting.
 * Open center - no pit / fire / pulse prop.
 */
export class ParkDrumCircle {
  readonly group = new THREE.Group();
  private readonly drummers: Drummer[] = [];

  constructor(
    cx: number,
    cz: number,
    groundY: (x: number, z: number) => number,
    count = 8,
  ) {
    const radius = 5.7;
    for (let i = 0; i < count; i++) {
      const ang = (i / count) * Math.PI * 2 - Math.PI / 2;
      const x = cx + Math.cos(ang) * radius;
      const z = cz + Math.sin(ang) * radius;
      const y = groundY(x, z);
      const variant = (i * 5 + 3) % 32;
      const ready = humanSprite("drumReady", variant);
      const hit = humanSprite("drumHit", variant);
      // Face the circle center.
      const facing = Math.atan2(cz - z, cx - x);

      const shadow = makeContactShadow({
        radius: Math.min(0.55, ready.width * 0.38),
        squash: 1,
        opacity: 0.85,
      });
      shadow.position.set(x, y + 0.02, z);

      const mesh = new THREE.Mesh(
        new THREE.PlaneGeometry(ready.width, ready.height),
        billboardMaterial(ready.texture),
      );
      mesh.position.set(x, y + ready.height * SPRITE_FOOT_Y, z);
      mesh.renderOrder = 3;

      this.drummers.push({
        mesh,
        shadow,
        x,
        z,
        y,
        facing,
        ready,
        hit,
        phase: i / count,
        period: 0.55 + (i % 3) * 0.08,
      });
      this.group.add(shadow, mesh);
    }
  }

  update(dtSec: number, timeMs: number, camera: THREE.Camera): void {
    for (const d of this.drummers) {
      d.phase = (d.phase + dtSec / d.period) % 1;
      const hitting = d.phase < 0.18 || d.phase > 0.92;
      setHumanMap(d.mesh, hitting ? d.hit : d.ready);
      const bob = 1 + Math.sin(timeMs * 0.008 + d.x) * 0.012;
      const bounce = hitting ? 0.04 : 0;
      d.mesh.position.y = d.y + d.ready.height * SPRITE_FOOT_Y * bob + bounce;
      faceCameraYaw(d.mesh, camera);
      const flip = facingFlipSign(d.x, d.z, d.facing, camera);
      d.mesh.scale.set(flip, 1, 1);
    }
  }

  destroy(): void {
    this.group.removeFromParent();
    for (const d of this.drummers) {
      d.mesh.geometry.dispose();
    }
    this.drummers.length = 0;
  }
}

function setHumanMap(
  mesh: THREE.Mesh,
  sheet: ReturnType<typeof humanSprite>,
): void {
  const mat = mesh.material;
  if (mat instanceof THREE.MeshBasicMaterial && mat.map !== sheet.texture) {
    mat.map = sheet.texture;
    mat.needsUpdate = true;
  }
}
